import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarClock, Loader2, Search, Zap, Flame, ExternalLink, Building, User as UserIcon, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { daysLabel } from "@/lib/scadenze";

const BUCKETS = [
  { id: "tutte", label: "Tutte (≤120 gg)" },
  { id: "scaduti", label: "Scadute" },
  { id: "30", label: "Entro 30 gg" },
  { id: "60", label: "Entro 60 gg" },
  { id: "90", label: "Entro 90 gg" },
  { id: "120", label: "Entro 120 gg" },
];

const LEVEL_TONE = {
  SCADUTO: "danger", URGENTE: "danger", ATTENZIONE: "warning",
  DA_CONTATTARE: "warning", DA_MONITORARE: "info", OK: "success",
};

export default function ScadenzePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [bucket, setBucket] = useState("tutte");
  const [type, setType] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [sending, setSending] = useState(false);

  const runReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/scadenze/reminders/run");
      toast.success(`Promemoria inviati: ${data.operators} operatori · ${data.admins} Direzione`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio promemoria non riuscito");
    } finally { setSending(false); }
  };

  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { bucket };
      if (type) params.type = type;
      if (officeId) params.office_id = officeId;
      if (operatorId) params.operator_id = operatorId;
      if (search) params.search = search;
      const { data } = await api.get("/scadenze", { params });
      setData(data);
    } catch (e) { setData({ items: [], counts: {} }); } finally { setLoading(false); }
  }, [bucket, type, officeId, operatorId, search]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load]);

  const counts = data.counts || {};

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="scadenze-page">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><CalendarClock className="h-6 w-6 text-brand" /> Forniture & Scadenze</h1>
        <p className="mt-1 text-sm text-slate-500">Contratti commodity in scadenza nel tuo perimetro. Contatta i clienti prima della scadenza delle condizioni economiche.</p>
      </div>

      {isAdmin && (
        <div className="ds-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid="reminders-bar">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Mail className="h-4 w-4 text-brand" />
            <span><span className="font-semibold text-slate-800">Promemoria automatici</span> — digest giornaliero (07:30) all'operatore assegnato + riepilogo Direzione di tutte le sedi.</span>
          </div>
          <Button size="sm" variant="outline" disabled={sending} onClick={runReminders} className="gap-1.5" data-testid="run-reminders">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Invia promemoria ora
          </Button>
        </div>
      )}

      {/* Fasce scadenza */}
      <div className="flex flex-wrap gap-2" data-testid="bucket-tabs">
        {BUCKETS.map((b) => (
          <button key={b.id} onClick={() => setBucket(b.id)} data-testid={`bucket-${b.id}`}
            className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
              bucket === b.id ? "border-brand bg-brand text-white" : "border-border bg-white text-slate-600 hover:border-brand/40"}`}>
            {b.label}
            <span className={`rounded-full px-1.5 text-xs font-bold ${bucket === b.id ? "bg-white/25" : "bg-slate-100 text-slate-500"}`}>{counts[b.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Filtri */}
      <div className="ds-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca cliente…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="scadenze-search" />
          </div>
          <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[130px]" data-testid="scadenze-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Luce e Gas</SelectItem>
              <SelectItem value="Luce">Luce</SelectItem>
              <SelectItem value="Gas">Gas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={officeId || "all"} onValueChange={(v) => setOfficeId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="scadenze-office"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutte le sedi</SelectItem>
              {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={operatorId || "all"} onValueChange={(v) => setOperatorId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px]" data-testid="scadenze-operator"><SelectValue placeholder="Operatore" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Tutti gli operatori</SelectItem>
                {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Tabella */}
      <div className="ds-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-500">
            <CalendarClock className="h-8 w-8 text-slate-300" /> Nessuna scadenza in questa fascia.
          </div>
        ) : (
          <div className="ds-table-scroll">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-slate-50 text-left">
                <tr className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Livello</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">POD/PDR</th>
                  <th className="px-4 py-3">Fornitore</th>
                  <th className="px-4 py-3">Sede</th>
                  <th className="px-4 py-3">Operatore</th>
                  <th className="px-4 py-3">Scadenza</th>
                  <th className="px-4 py-3">Residui</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((i) => (
                  <tr key={i.contract_id} className="hover:bg-brand/5" data-testid={`scadenza-row-${i.contract_id}`}>
                    <td className="px-4 py-3"><StatusBadge type={LEVEL_TONE[i.level] || "neutral"} icon={CalendarClock}>{i.label}</StatusBadge></td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{i.customer_name}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-slate-600">
                        {i.supply_type === "Luce" ? <Zap className="h-3.5 w-3.5 text-warning" /> : <Flame className="h-3.5 w-3.5 text-brand" />} {i.supply_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{i.pod_pdr_masked}</td>
                    <td className="px-4 py-3 text-slate-600">{i.supplier || "—"}</td>
                    <td className="px-4 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Building className="h-3.5 w-3.5 text-slate-400" />{i.office_name || "—"}</span></td>
                    <td className="px-4 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><UserIcon className="h-3.5 w-3.5 text-slate-400" />{i.operator_name || "—"}</span></td>
                    <td className="px-4 py-3 text-slate-600">{new Date(i.economic_end_date).toLocaleDateString("it-IT")}</td>
                    <td className="px-4 py-3 font-medium text-slate-700">{daysLabel(i.days_to_expiry)}</td>
                    <td className="px-4 py-3 text-slate-600">{i.contract_status}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => navigate(`/clienti/${i.customer_id}`)} data-testid={`open-customer-${i.contract_id}`}>
                        <ExternalLink className="h-3.5 w-3.5" /> Apri
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
